import { useState, useEffect, useCallback } from 'react';
import { 
  extractUTMParams, 
  saveUTMToSession, 
  getUTMFromSession, 
  hasUTMParams,
  isUTMFresh,
  clearUTMFromSession,
  getUTMTimestamp
} from './utmHelpers';

/**
 * Custom hook for UTM parameter tracking
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoCapture - Automatically capture UTM on mount (default: true)
 * @param {number} options.maxAge - Maximum age for UTM parameters in milliseconds (default: 30 minutes)
 * @param {boolean} options.persistAcrossRoutes - Keep UTM across route changes (default: true)
 * @returns {Object} UTM tracking state and methods
 */
export function useUTMTracking(options = {}) {
  const {
    autoCapture = true,
    maxAge = 30 * 60 * 1000, // 30 minutes
    persistAcrossRoutes = true
  } = options;

  const [utmParams, setUtmParams] = useState({});
  const [isTracking, setIsTracking] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  /**
   * Capture UTM parameters from current URL
   */
  const captureUTM = useCallback(() => {
    try {
      console.log('captureUTM called');
      const params = extractUTMParams();
      console.log('Extracted params:', params);
      
      if (Object.keys(params).length > 0) {
        console.log('Capturing UTM parameters:', params);
        saveUTMToSession(params);
        setUtmParams(params);
        setLastUpdated(Date.now());
        setIsTracking(true);
        return params;
      } else {
        console.log('No UTM parameters found in URL');
        return {};
      }
    } catch (error) {
      console.error('Error capturing UTM parameters:', error);
      return {};
    }
  }, []);

  /**
   * Load UTM parameters from sessionStorage
   */
  const loadUTMFromSession = useCallback(() => {
    try {
      const params = getUTMFromSession();
      
      if (Object.keys(params).length > 0 && isUTMFresh(maxAge)) {
        console.log('Loading UTM parameters from session:', params);
        setUtmParams(params);
        setLastUpdated(getUTMTimestamp());
        setIsTracking(true);
        return params;
      } else {
        console.log('No valid UTM parameters in session');
        if (!isUTMFresh(maxAge)) {
          clearUTMFromSession();
        }
        setUtmParams({});
        setLastUpdated(null);
        setIsTracking(false);
        return {};
      }
    } catch (error) {
      console.error('Error loading UTM from session:', error);
      return {};
    }
  }, [maxAge]);

  /**
   * Clear UTM parameters
   */
  const clearUTM = useCallback(() => {
    clearUTMFromSession();
    setUtmParams({});
    setLastUpdated(null);
    setIsTracking(false);
    console.log('UTM parameters cleared');
  }, []);

  /**
   * Check if UTM parameters are fresh
   */
  const checkUTMFreshness = useCallback(() => {
    return isUTMFresh(maxAge);
  }, [maxAge]);

  /**
   * Get specific UTM parameter value
   */
  const getUTMValue = useCallback((key) => {
    return utmParams[key] || null;
  }, [utmParams]);

  /**
   * Check if specific UTM parameter exists
   */
  const hasUTMValue = useCallback((key) => {
    return !!utmParams[key];
  }, [utmParams]);

  // Auto-capture UTM parameters on mount
  useEffect(() => {
    console.log('UTM Tracking useEffect triggered:', { autoCapture });
    if (autoCapture) {
      // First try to load from session
      const sessionParams = loadUTMFromSession();
      console.log('Session params loaded:', sessionParams);
      
      // If no valid session data, capture from current URL
      if (Object.keys(sessionParams).length === 0) {
        console.log('No session data, capturing from URL');
        captureUTM();
      }
    }
  }, [autoCapture, captureUTM, loadUTMFromSession]);

  // Handle route changes if persistAcrossRoutes is true
  useEffect(() => {
    if (persistAcrossRoutes && typeof window !== 'undefined') {
      const handleRouteChange = () => {
        // Check if we should capture new UTM parameters
        const currentParams = extractUTMParams();
        if (Object.keys(currentParams).length > 0) {
          captureUTM();
        }
      };

      // Listen for popstate (browser back/forward)
      window.addEventListener('popstate', handleRouteChange);
      
      return () => {
        window.removeEventListener('popstate', handleRouteChange);
      };
    }
  }, [persistAcrossRoutes, captureUTM]);

  return {
    // State
    utmParams,
    isTracking,
    lastUpdated,
    
    // Methods
    captureUTM,
    loadUTMFromSession,
    clearUTM,
    checkUTMFreshness,
    getUTMValue,
    hasUTMValue,
    
    // Convenience getters for common UTM parameters
    source: utmParams.utm_source,
    medium: utmParams.utm_medium,
    campaign: utmParams.utm_campaign,
    term: utmParams.utm_term,
    content: utmParams.utm_content,
    id: utmParams.utm_id
  };
}
